import MRIMachineForm from './MRIMachineForm';
import { useParams, useNavigate } from 'react-router-dom';
import { getMRIMachineById } from '../../services/apiService';
import { useEffect, useState } from 'react';
import LoadingRing from '../ui-components/LoadingRing';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { MRIMachineFormData, MRIMachinePopulated } from './types';

export default function AddMRIMachine() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [machine, setMachine] = useState<MRIMachinePopulated | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const { id } = useParams<{ id: string }>();

    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);

    const getData = async () => {
        if (!id || id === 'create-new-modal' || !isValidObjectId(id)) {
            setIsLoading(false);
            return;
        }

        try {
            const machineData = await getMRIMachineById(id);
            setMachine(machineData);
        } catch (error) {
            console.error('Error fetching MRI Machine:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="h-full flex justify-center items-center">
                <LoadingRing size="large" />
            </div>
        );
    }

    const defaultMachine: MRIMachineFormData = {
        Name: '',
        Model: machine?.Model || '',
        Manufacturer: machine?.Manufacturer || '',
        FieldStrength: machine?.FieldStrength || 1.5,
        SpatialGradient: machine?.SpatialGradient || [],
        SlewRatePerAxis: machine?.SlewRatePerAxis || 0,
        MaxSlewRate: machine?.MaxSlewRate || 0,
        MaxSAR: machine?.MaxSAR || [],
        MaxB1rms: machine?.MaxB1rms || '',
        TRCoils: machine?.TRCoils || '',
        attachedFiles: [],
        machineImage: '',
        notes: '',
    };

    return (
        <div className="mx-auto max-w-3xl px-4 pt-6 pb-28 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight mb-1">
                        {t('roomOverview.addMRIMachine.title')}
                    </h1>
                    <p className="text-muted-foreground">
                        {machine
                            ? t('roomOverview.addMRIMachine.templateSubtitle', {
                                  model: machine.Model,
                              })
                            : t('roomOverview.addMRIMachine.newSubtitle')}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate('/room-overview/create-room')}
                >
                    <ArrowLeft className="h-4 w-4" />
                    {t('roomOverview.addMRIMachine.backButton')}
                </Button>
            </div>

            {(machine || id === 'create-new-modal') && (
                <MRIMachineForm MRImachine={defaultMachine} isNew={true} />
            )}
        </div>
    );
}
